import { TagModel, WorklistViewModel } from 'models';

import { PatientRoutes } from '../routes';

export interface BreadcrumbItem {
  id: number | null;
  name: string;
  path: string;
  isRoot?: boolean;
  isVirtual?: boolean; // For non-clickable items like stack names
}

export interface NavigationContext {
  patientId: number;
  currentPathname: string;
  selectedWorklist?: WorklistViewModel | null;
  allWorklists?: WorklistViewModel[] | null;
  stackName?: string;
  hierarchicalTags?: TagModel[] | null;
  shareContext?: {
    shareLinkId: string;
    isShareMode: true;
  };
}

/**
 * Slugify helper for consistent URL generation
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/**
 * Extract tag IDs from worklist oDataResourceString
 * Keep this for compatibility with existing tag-based filtering logic
 */
export function extractWorklistTagIds(worklist: WorklistViewModel): number[] {
  if (!worklist.oDataResourceString) return [];

  const tagIdMatch = /TagId~in~([\d,]+)/.exec(worklist.oDataResourceString);
  if (!tagIdMatch?.[1]) return [];

  return tagIdMatch[1]
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map(Number)
    .filter((id) => !isNaN(id));
}

/**
 * Find child worklists using the parentViewId relationship
 * This replaces the complex tag-based hierarchy logic
 */
export function findChildWorklists(parentWorklist: WorklistViewModel, allWorklists: WorklistViewModel[]): WorklistViewModel[] {
  return allWorklists.filter((worklist) => worklist.parentViewId === parentWorklist.id);
}

/**
 * Get the complete hierarchy path for a worklist (from root to current)
 */
export function getWorklistHierarchy(worklist: WorklistViewModel, allWorklists: WorklistViewModel[]): WorklistViewModel[] {
  const hierarchy: WorklistViewModel[] = [];
  let current: WorklistViewModel | undefined = worklist;

  while (current) {
    hierarchy.unshift(current);
    current = current.parentViewId ? allWorklists.find((w) => w.id === current?.parentViewId) : undefined;
  }

  return hierarchy;
}

/**
 * Get all descendant worklists for a given worklist
 */
export function getAllDescendantWorklists(parentWorklist: WorklistViewModel, allWorklists: WorklistViewModel[]): WorklistViewModel[] {
  const descendants: WorklistViewModel[] = [];
  const directChildren = findChildWorklists(parentWorklist, allWorklists);

  for (const child of directChildren) {
    descendants.push(child);
    descendants.push(...getAllDescendantWorklists(child, allWorklists));
  }

  return descendants;
}

/**
 * Check if a file belongs to a specific worklist based on its tags
 * Keep this for compatibility with existing filtering logic
 */
export function fileMatchesWorklist(fileTagIds: Set<number>, worklist: WorklistViewModel, hierarchicalTags: TagModel[]): boolean {
  const worklistTagIds = extractWorklistTagIds(worklist);
  if (worklistTagIds.length === 0) return false;

  // Simple check if any file tag matches any worklist tag
  return worklistTagIds.some((tagId) => fileTagIds.has(tagId));
}

/**
 * Parse current URL to determine navigation context
 */
export function parseNavigationContext(context: NavigationContext): {
  isInWorklist: boolean;
  isViewingFile: boolean;
  currentWorklist?: WorklistViewModel;
  fileId?: string;
  worklistSlug?: string;
} {
  const { patientId, currentPathname, allWorklists, shareContext } = context;
  const filesBasePath = shareContext?.isShareMode ? `/share/${shareContext.shareLinkId}/files` : PatientRoutes.getFiles(patientId);

  // Enhanced regex to match both worklist file patterns and worklist navigation patterns
  const worklistFileRegex = new RegExp(`${filesBasePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/([^/]+)/file/(\\d+)`);
  const worklistNavRegex = new RegExp(`${filesBasePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/([^/]+)(?:/|$)`);

  // For share context, also check for /view/ patterns
  let shareViewFileMatch = null;
  if (shareContext?.isShareMode) {
    const shareViewFileRegex = new RegExp(`/share/${shareContext.shareLinkId}/view/(\\d+)`);
    shareViewFileMatch = shareViewFileRegex.exec(currentPathname);
  }

  const worklistFileMatch = worklistFileRegex.exec(currentPathname);
  const worklistNavMatch = worklistNavRegex.exec(currentPathname);

  // Handle share context file viewing (e.g., /share/xxx/view/12345)
  if (shareViewFileMatch?.[1]) {
    const fileId = shareViewFileMatch[1];
    return {
      isInWorklist: false,
      isViewingFile: true,
      fileId,
    };
  }

  if (worklistFileMatch?.[1] && worklistFileMatch?.[2]) {
    const worklistSlug = worklistFileMatch[1];
    const fileId = worklistFileMatch[2];

    const worklist = allWorklists?.find((w) => {
      const basicSlug = slugify(w.name);
      return basicSlug === worklistSlug;
    });

    return {
      isInWorklist: true,
      isViewingFile: true,
      currentWorklist: worklist,
      fileId,
      worklistSlug,
    };
  }

  if (worklistNavMatch?.[1] && !worklistFileMatch) {
    const worklistSlug = worklistNavMatch[1];

    const worklist = allWorklists?.find((w) => {
      const basicSlug = slugify(w.name);
      return basicSlug === worklistSlug;
    });

    return {
      isInWorklist: true,
      isViewingFile: false,
      currentWorklist: worklist,
      worklistSlug,
    };
  }

  return {
    isInWorklist: false,
    isViewingFile: false,
  };
}

/**
 * Generate breadcrumbs based on worklist hierarchy using parentViewId
 */
export function generateBreadcrumbs(context: NavigationContext): BreadcrumbItem[] {
  const { patientId, selectedWorklist, stackName, allWorklists, shareContext } = context;
  const navContext = parseNavigationContext(context);
  const filesBasePath = shareContext?.isShareMode ? `/share/${shareContext.shareLinkId}/files` : PatientRoutes.getFiles(patientId);

  // For share context, the home breadcrumb should go to the share root (insight view)
  // For regular patient context, it should go to the files root
  const homePath = shareContext?.isShareMode ? `/share/${shareContext.shareLinkId}/insight` : filesBasePath;

  const items: BreadcrumbItem[] = [
    {
      id: null,
      name: '',
      path: homePath,
      isRoot: true,
    },
  ];

  // Determine which worklist to use for breadcrumbs
  const targetWorklist = navContext.currentWorklist || selectedWorklist;

  if (targetWorklist && allWorklists) {
    // Get the complete hierarchy for this worklist
    const hierarchy = getWorklistHierarchy(targetWorklist, allWorklists);

    // Add each level of the hierarchy to breadcrumbs
    for (const worklist of hierarchy) {
      const worklistSlug = slugify(worklist.name);
      items.push({
        id: worklist.id,
        name: worklist.name,
        path: `${filesBasePath}/${worklistSlug}`,
        isVirtual: !!stackName, // Don't allow clicking if we're showing a stack overlay
      });
    }

    if (stackName) {
      items.push({
        id: -1,
        name: stackName,
        path: context.currentPathname,
        isVirtual: true,
      });
    }

    if (navContext.isViewingFile && navContext.fileId) {
      items.push({
        id: parseInt(navContext.fileId),
        name: `File ${navContext.fileId}`,
        path: context.currentPathname,
        isVirtual: true,
      });
    }
  } else if (navContext.isViewingFile && navContext.fileId) {
    // Handle case where we're viewing a file but not in a specific worklist context
    // This is common in share mode when viewing files directly
    items.push({
      id: parseInt(navContext.fileId),
      name: `File ${navContext.fileId}`,
      path: context.currentPathname,
      isVirtual: true,
    });
  }

  return items;
}

/**
 * Determine if breadcrumbs should be shown
 */
export function shouldShowBreadcrumb(breadcrumbs: BreadcrumbItem[]): boolean {
  return breadcrumbs.length > 1;
}

/**
 * Generate worklist URL for navigation
 */
export function getWorklistUrl(patientId: number, worklist: WorklistViewModel): string {
  const filesBasePath = PatientRoutes.getFiles(patientId);
  const worklistSlug = slugify(worklist.name);
  return `${filesBasePath}/${worklistSlug}`;
}

/**
 * Generate file URL within worklist context
 */
export function getFileInWorklistUrl(patientId: number, worklist: WorklistViewModel, fileId: string | number): string {
  const worklistSlug = slugify(worklist.name);
  return PatientRoutes.getFileViewInWorklist(patientId, worklistSlug, fileId);
}

/**
 * Get the hierarchical path for a tag using its parent relationships
 */
export function getTagPath(tagId: number, flatTagsMap: Map<number, TagModel>): string {
  const path: string[] = [];
  let currentTag = flatTagsMap.get(tagId);
  while (currentTag) {
    path.unshift(slugify(currentTag.name));
    currentTag = currentTag.parentTagId ? flatTagsMap.get(currentTag.parentTagId) : undefined;
  }
  return path.join('/');
}
